import type { FeatureLike } from "ol/Feature"
import Feature from "ol/Feature"
import type OlMap from "ol/Map"
import Point from "ol/geom/Point"
import Polygon from "ol/geom/Polygon"
import { Vector as VectorLayer } from "ol/layer"
import { fromLonLat } from "ol/proj"
import { Vector as VectorSource } from "ol/source"
import { Fill, Stroke, Style, Text } from "ol/style"
import CircleStyle from "ol/style/Circle"
import { useTheme } from "vuetify"

export const useRenderClusters = (
  map: Ref<OlMap | null>,
  initialized: Ref<boolean>,
  clusters: Ref<Array<GeoCluster> | undefined>,
  clusterZoomLevel: number,
  boundsByKey: Ref<BoundsByKey>,
  fitMapToVisibleMarkers: (fromClusters?: boolean, clusters?: MaybeRef<Array<GeoCluster> | undefined>) => void,
  forceInteract: () => void,
  refitAfterLoadOfCluster: Ref<boolean>,
) => {
  const { removeLayer, getLayer } = useLayerManagement(map)

  const theme = useTheme()
  const { resetBounds, updateBounds } = useFeatureBounds(boundsByKey)

  const updateClusterLayer = async () => {
    if (!map.value) {
      return
    }

    removeLayer(OpenLayersLayer.MARKER_CLUSTER)
    removeLayer(OpenLayersLayer.MARKER_CLUSTER_CONVEX_HULL)

    // Reset bounds since everything is reset
    resetBounds(["clusters"])

    //  Don't do anything if no clusters are given
    if (!clusters.value?.length) {
      return
    }

    const clusterFeatures: Array<Feature<Point>> = []
    const clusterConvexHullFeatures: Array<Feature<Polygon>> = []

    for (const cluster of clusters.value) {
      const clusterFeature = new Feature({
        geometry: new Point(fromLonLat(cluster.center)),
        group_id: cluster.name,
        type: OpenLayersLayer.MARKER_CLUSTER,
        size: cluster.count,
        name: cluster.name,
      })

      const coordinates = cluster.convexHull.map((coordinates) => fromLonLat(coordinates))

      const clusterConvexHullFeature = new Feature({
        geometry: new Polygon([coordinates]),
        group_id: cluster.name + "_convex_hull",
        type: OpenLayersLayer.MARKER_CLUSTER_CONVEX_HULL,
        size: cluster.count,
        name: cluster.name,
        hidden: true,
      })

      clusterFeatures.push(clusterFeature)
      clusterConvexHullFeatures.push(clusterConvexHullFeature)

      updateBounds("clusters", cluster.center[1], cluster.center[0])
    }

    // 3. Create vector source and layer
    const clusterVectorSource = new VectorSource({
      features: clusterFeatures,
    })

    const clusterLayer = new VectorLayer({
      source: clusterVectorSource,
      maxZoom: clusterZoomLevel,
      style: getClusterStyle,
    })

    // 3. Create vector source and layer
    const ClusterConvexHullVectorSource = new VectorSource({
      features: clusterConvexHullFeatures,
    })

    const clusterConvexHullLayer = new VectorLayer({
      source: ClusterConvexHullVectorSource,
      maxZoom: clusterZoomLevel,
      style: null,
    })

    clusterLayer.set("name", OpenLayersLayer.MARKER_CLUSTER)
    clusterConvexHullLayer.set("name", OpenLayersLayer.MARKER_CLUSTER_CONVEX_HULL)

    map.value.addLayer(clusterConvexHullLayer)
    map.value.addLayer(clusterLayer)

    if (!initialized.value) {
      // Not pretty but this is the only way to make sure the map has fully loaded
      await new Promise((resolve) => setTimeout(resolve, 100))
      fitMapToVisibleMarkers(true, clusters.value)
    }

    forceInteractionIfZoomLarge()
  }

  const forceInteractionIfZoomLarge = () => {
    if (!map.value) return

    const view = map.value.getView()

    const newZoom = view.getZoom() ?? 0

    // force interact emit if zoom is greater than cluster zoom level
    // since geo assets should be fetched as they should be shown
    if (newZoom > clusterZoomLevel) {
      refitAfterLoadOfCluster.value = true
      forceInteract()
      return
    }

    initialized.value = true
  }

  const getClusterStyle = (feature: FeatureLike): Style | Style[] => {
    const size = feature.get("size")

    const innerCircle = new CircleStyle({
      radius: 16,
      fill: new Fill({
        color: theme.current.value.colors.secondary + "95",
      }),
    })

    const outerCircle = new CircleStyle({
      radius: 12,
      fill: new Fill({
        color: theme.current.value.colors.primary + "70",
      }),
    })

    // Cluster style
    return [
      new Style({
        image: outerCircle,
      }),
      new Style({
        image: innerCircle,
        text: new Text({
          text: size.toString(),
          fill: new Fill({ color: "#fff" }),
          offsetY: 1,
          offsetX: 1,
        }),
      }),
    ]
  }

  const setConvexHullOnHover = (hoveredFeatures: Array<FeatureLike>) => {
    const hoveredClusters = hoveredFeatures.filter((feature) => feature.get("type") === OpenLayersLayer.MARKER_CLUSTER)
    const shownConvexHullIds = hoveredClusters.map((feature) => (feature.get("group_id") as string) + "_convex_hull")

    const convexHullLayer = getLayer(OpenLayersLayer.MARKER_CLUSTER_CONVEX_HULL) as VectorLayer

    const allConvexHulls = convexHullLayer?.getSource()?.getFeatures() ?? []

    const convexHullsToShow = allConvexHulls.filter(
      (feature) => feature.get("hidden") === true && shownConvexHullIds.includes(feature.get("group_id") as string),
    )

    convexHullsToShow.forEach((feature) => showConvexHull(feature))

    const convexHullsToHide = allConvexHulls.filter(
      (feature) => feature.get("hidden") === false && !shownConvexHullIds.includes(feature.get("group_id") as string),
    )

    convexHullsToHide.forEach((feature) => hideConvexHull(feature))
  }

  const showConvexHull = (feature: FeatureLike) => {
    if (feature.get("type") !== OpenLayersLayer.MARKER_CLUSTER_CONVEX_HULL || !("set" in feature)) {
      return
    }

    feature.set("hidden", false)

    feature.setStyle(getClusterConvexHullStyle(feature))
  }

  const hideConvexHull = (feature: FeatureLike) => {
    if (feature.get("type") !== OpenLayersLayer.MARKER_CLUSTER_CONVEX_HULL || !("set" in feature)) {
      return
    }

    feature.set("hidden", true)

    feature.setStyle(undefined)
  }

  const getClusterConvexHullStyle = (_feature: FeatureLike): Style | Style[] => {
    return new Style({
      stroke: new Stroke({
        color: theme.current.value.colors.secondary + "95",
        width: 3,
      }),
      fill: new Fill({
        color: theme.current.value.colors.primary + "70",
      }),
    })
  }

  watch(
    clusters,
    () => {
      updateClusterLayer()
    },
    { immediate: true },
  )

  return {
    setConvexHullOnHover,
    getClusterStyle,
  }
}
